import { useEffect } from "react";
import {
  getCreditNotesPdf,
  getDocumentPdf,
  getDocumentsClient,
  getInvoicePdf,
} from "../../api/clientPortal";
import { useCustomerAreaStore } from "../../stores/customerArea";
import Accordion from "../../components/_espaceClient/Accordion";
import { showToastError } from "../../services/toast";
import { useTranslation } from "react-i18next";
import {
  PageHeader,
  PagePanel,
  PageShell,
} from "../../components/ui/page/PageShell";
import { TableEmptyState } from "../../components/ui/tables/tableUi";

const CustomerArea = () => {
  const { t } = useTranslation();
  const { creditNotes, setCreditNotes, documents, setDocuments } =
    useCustomerAreaStore();

  // Récupération des factures par client
  useEffect(() => {
    getDocumentsClient()
      .then((res) => {
        setDocuments(res.data);

        // Extraire toutes les notes de crédit
        const allCreditNotes = res.data.flatMap((customer: any) =>
          customer.invoices.flatMap((invoice: any) => invoice.creditNote)
        );

        setCreditNotes(allCreditNotes);
      })
      .catch((err) => {
        showToastError(err.response.data.message)
      });
  }, []);

  const handleDownloadInvoices = (item: any) => {
    getInvoicePdf(item.id)
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = item.documentName || `invoice_${item.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        showToastError(err.response.data.message)
      });
  };

  const handleDownloadCreditNote = (item: any) => {
    getCreditNotesPdf(item.id)
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = item.documentName || `creditNote_${item.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        showToastError(err.response.data.message)
    });
  };

  const handleDownloadDocument = (item: any) => {
    getDocumentPdf(item.id)
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = item.documentName || `document_${item.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        showToastError(err.response.data.message)
      });
  };

  const handleViewDocument = (item: any) => {
    getDocumentPdf(item.id)
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Nettoyer l'URL après un délai pour libérer la mémoire
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      })
      .catch((err) => {
        showToastError(err.response.data.message)
      });
  };

  const handleViewInvoice = (item: any) => {
    getInvoicePdf(item.id)
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Nettoyer l'URL après un délai pour libérer la mémoire
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      })
      .catch((err) => {
        showToastError(err.response.data.message)
      });
  };

  const handleViewCreditNote = (item: any) => {
    getCreditNotesPdf(item.id)
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Nettoyer l'URL après un délai pour libérer la mémoire
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      })
      .catch((err) => {
        showToastError(err.response.data.message)
      });
  };

  return (
    <PageShell>
      <PageHeader
        eyebrow={t("Espace client")}
        title={t("Votre espace de facturation")}
        description={t("Consultez et téléchargez vos documents.")}
      />

      <PagePanel>
        <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-5">
          {documents.length > 0 ? (
            <div className="flex flex-col gap-6">
              {documents.map((customer: any) => (
                <section key={customer.id}>
                  <h2 className="mb-2.5 flex items-center gap-2 px-1">
                    <span
                      aria-hidden="true"
                      className="size-1.5 shrink-0 rounded-full bg-indigo-500"
                    />
                    <span className="truncate font-Nunito text-base font-bold text-slate-900">
                      {customer.name}
                    </span>
                  </h2>

                  <div className="flex flex-col gap-2">
                    <Accordion
                      title={t("Factures")}
                      data={customer.invoices}
                      onDownload={handleDownloadInvoices}
                      onView={handleViewInvoice}
                    />
                    <Accordion
                      title={t("Notes de crédit")}
                      data={creditNotes}
                      onDownload={handleDownloadCreditNote}
                      onView={handleViewCreditNote}
                    />
                    <Accordion
                      title="Documents"
                      data={customer.documents}
                      onDownload={handleDownloadDocument}
                      onView={handleViewDocument}
                    />
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <TableEmptyState
              title={t("Aucune facture trouvée")}
              description={t(
                "Vos documents de facturation apparaîtront ici"
              )}
            />
          )}
        </div>
      </PagePanel>
    </PageShell>
  );
};

export default CustomerArea;
